import { React, classNames } from 'libs/react';
import { DealPaymentItem } from './DealPaymentItem';
import { DealPaymentSplitModal } from './DealPaymentSplitModal';
import { Button } from 'components/Button';
import { Text } from 'components/Text';
import { Money } from 'components/Money';
import { useModal } from 'components/modal/ModalStack';
import { useDealConfiguratorContext } from 'modules/deal/DealConfigureScreen';
import { applyPercentPaymentAmounts, paymentScheduleBalance, settlePaymentScheduleBalance } from 'helpers/DealPaymentSchedule';
import './DealPaymentPanel.scss';
function parsePaymentInfos(infos) {
    if (!infos) {
        return {};
    }
    if (typeof infos === 'object') {
        return infos;
    }
    try {
        const parsed = JSON.parse(infos);
        if (parsed == null || typeof parsed !== 'object') {
            return {};
        }
        return parsed;
    }
    catch (_a) {
        return {};
    }
}
function paymentOrderId(infos) {
    const orderId = parsePaymentInfos(infos).orderId;
    return orderId != null && String(orderId) !== '' ? String(orderId) : null;
}
function isUpsellPaymentInfos(infos) {
    return parsePaymentInfos(infos).kind === 'upsell';
}
function sortByDate(values) {
    return [...values].sort((a, b) => {
        var _a, _b, _c, _d;
        return String((_b = (_a = a.date) !== null && _a !== void 0 ? _a : a.createdAt) !== null && _b !== void 0 ? _b : '').localeCompare(String((_d = (_c = b.date) !== null && _c !== void 0 ? _c : b.createdAt) !== null && _d !== void 0 ? _d : ''));
    });
}
export function paymentsForOrder(payments, orders, orderId) {
    if (payments.length === 0) {
        return [];
    }
    const matchedById = payments.filter((payment) => paymentOrderId(payment.infos) === orderId);
    if (matchedById.length > 0) {
        return matchedById;
    }
    const sortedOrders = sortByDate(orders !== null && orders !== void 0 ? orders : []);
    const index = sortedOrders.findIndex((order) => order.id === orderId);
    if (sortedOrders.length <= 1 || index < 0) {
        return payments;
    }
    const upsellPayments = payments.filter((payment) => isUpsellPaymentInfos(payment.infos));
    const primaryPayments = payments.filter((payment) => !isUpsellPaymentInfos(payment.infos));
    if (index === 0 && primaryPayments.length > 0) {
        return primaryPayments;
    }
    if (index > 0 && upsellPayments.length > 0) {
        const extra = sortByDate(upsellPayments)[index - 1];
        return extra ? [extra] : upsellPayments;
    }
    const paired = sortByDate(payments)[index];
    return paired ? [paired] : payments;
}
/**
 * @component DealPaymentPanel
 * @since 1.0.0
 */
export function DealPaymentPanel(props) {
    var _a;
    const { deal, dealTotal, editable, hasDeposit, orderId, showPaymentDetails } = props;
    const modal = useModal();
    const { updatePayments } = useDealConfiguratorContext();
    const total = dealTotal !== null && dealTotal !== void 0 ? dealTotal : 0;
    const allPayments = (_a = deal === null || deal === void 0 ? void 0 : deal.payments) !== null && _a !== void 0 ? _a : [];
    // Percent-based payments follow the total shown on screen, e.g. when the
    // client selects a proposed option on the quote. Per-order totals are not
    // the base of those percents, so the order view keeps the saved amounts.
    const payments = orderId
        ? paymentsForOrder(allPayments, deal === null || deal === void 0 ? void 0 : deal.orders, orderId)
        : editable || !total
            ? allPayments
            : applyPercentPaymentAmounts(allPayments, total);
    const balance = paymentScheduleBalance(payments, total);
    const hasBalance = Math.abs(balance) >= 0.005;
    /**
     * @function onPress
     * @since 1.0.o
     * @hidden
     */
    const onPress = () => {
        updatePayments([
            ...allPayments,
            {
                id: '',
                date: null,
                description: null,
                createdAt: '',
                updatedAt: '',
                status: 'INACTIVE',
                amount: 0
            }
        ]);
    };
    /**
     * @function onSettlePress
     * @since 1.0.0
     * @hidden
     */
    const onSettlePress = () => {
        updatePayments(settlePaymentScheduleBalance(allPayments, total));
    };
    /**
     * @function onSplitPress
     * @since 1.0.0
     * @hidden
     */
    const onSplitPress = () => {
        modal.present({
            style: 'dialog',
            title: t('Split payments'),
            picto: 'credit-card',
            modal: (React.createElement(DealPaymentSplitModal, { deal: deal, hasDeposit: hasDeposit, total: total, onApply: updatePayments }))
        });
    };
    return (React.createElement("div", { className: "deal-payment-panel" },
        payments.length > 0 && (React.createElement(React.Fragment, null,
            !editable && (React.createElement("div", { className: "deal-payment-panel-heading" },
                React.createElement(Text, { className: "deal-payment-panel-title", value: t('Payments') }))),
            editable ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "deal-payment-panel-table-heading" },
                    hasDeposit && (React.createElement("div", { className: "deal-payment-panel-table-heading-item deal-payment-panel-table-heading-item--deposit" },
                        React.createElement(Text, { value: t('Dépôt') }))),
                    React.createElement("div", { className: "deal-payment-panel-table-heading-item deal-payment-panel-table-heading-item--date" },
                        React.createElement(Text, { value: t('Date') })),
                    React.createElement("div", { className: "deal-payment-panel-table-heading-item deal-payment-panel-table-heading-item--description" },
                        React.createElement(Text, { value: t('Description') })),
                    React.createElement("div", { className: "deal-payment-panel-table-heading-item deal-payment-panel-table-heading-item--status" },
                        React.createElement(Text, { value: t('Status') })),
                    React.createElement("div", { className: "deal-payment-panel-table-heading-item deal-payment-panel-table-heading-item--amount" },
                        React.createElement(Text, { value: t('Amount') })),
                    React.createElement("div", { className: "deal-payment-panel-table-heading-spacer" })),
                React.createElement("div", { className: "deal-payment-panel-table" }, payments === null || payments === void 0 ? void 0 : payments.map((payment, index) => (React.createElement(DealPaymentItem, { deal: deal, payment: payment, dealTotal: dealTotal, editable: editable, hasDeposit: hasDeposit, key: payment.id || `payment-${index}` })))))) : (React.createElement("div", { className: "deal-payment-panel-quote-table-wrap" },
                React.createElement("table", { className: "deal-payment-panel-quote-table" },
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            hasDeposit && (React.createElement("th", { className: "deal-payment-quote-th deal-payment-quote-th--deposit", scope: "col" },
                                React.createElement(Text, { value: t('Deposit') }))),
                            React.createElement("th", { className: "deal-payment-quote-th deal-payment-quote-th--date", scope: "col" },
                                React.createElement(Text, { value: t('Date') })),
                            React.createElement("th", { className: "deal-payment-quote-th deal-payment-quote-th--description", scope: "col" },
                                React.createElement(Text, { value: t('Description') })),
                            React.createElement("th", { className: "deal-payment-quote-th deal-payment-quote-th--status", scope: "col" },
                                React.createElement(Text, { value: t('Status') })),
                            React.createElement("th", { className: "deal-payment-quote-th deal-payment-quote-th--percent", scope: "col" },
                                React.createElement("div", { className: "deal-payment-quote-th-percent" },
                                    React.createElement(Text, { value: "%" }))),
                            React.createElement("th", { className: "deal-payment-quote-th deal-payment-quote-th--price", scope: "col" },
                                React.createElement(Text, { value: t('Amount') })),
                            showPaymentDetails && (React.createElement("th", { className: "deal-payment-quote-th deal-payment-quote-th--details", scope: "col", "aria-label": t('Payment details') })))),
                    React.createElement("tbody", null, payments === null || payments === void 0 ? void 0 : payments.map((payment, index) => (React.createElement(DealPaymentItem, { deal: deal, payment: payment, dealTotal: dealTotal, editable: editable, hasDeposit: hasDeposit, showPaymentDetails: showPaymentDetails, key: payment.id || `payment-${index}` }))))))))),
        editable && payments.length > 0 && (React.createElement("div", { className: classNames('deal-payment-panel-summary', {
                'deal-payment-panel-summary--unbalanced': hasBalance
            }) },
            React.createElement("div", { className: "deal-payment-panel-summary-row" },
                React.createElement(Text, { value: t('Scheduled') }),
                React.createElement(Money, { value: total - balance })),
            React.createElement("div", { className: "deal-payment-panel-summary-row" },
                React.createElement(Text, { value: t('Total') }),
                React.createElement(Money, { value: total })),
            React.createElement("div", { className: "deal-payment-panel-summary-row deal-payment-panel-summary-row--balance" },
                React.createElement(Text, { value: t('Remaining balance') }),
                React.createElement(Money, { value: balance })),
            hasBalance && (React.createElement(Button, { style: "outline", size: "small", label: t('Adjust last payment'), onPress: onSettlePress })))),
        editable && (React.createElement("div", { className: "deal-payment-panel-footer" },
            React.createElement(Button, { style: "outline", label: t('Add Payment'), onPress: onPress }),
            React.createElement(Button, { style: "outline", label: t('Split'), onPress: onSplitPress })))));
}
